import genResource from './helpers/genResource';
import {
  MOCK_API_URL,
  LOCAL_ROOT,
  CROPWISE_PROXY_URL,
  CROPWISE_PROXY_V2_URL,
  MASTERLIST_CROPS_URL,
  MASTERLIST_PROXY_URL,
  CROPWISE_MASTERDATA,
  AG_CLEAR_APP_URL
} from './apiConstants';
import makeRoutedResource from './helpers/makeRoutedResource';
import {
  INVOICE_V2_RESOURCE_NAME,
  isInvoicesV2Enabled,
  remapInvoiceAction
} from './invoiceServiceV2';
import {
  RECOMMENDATION_V2_RESOURCE_NAME,
  isJavaOnlyRecommendationAction,
  isRecommendationsV2Enabled,
  remapRecommendationAction
} from './recommendationServiceV2';
import {
  ORGANIZATION_V2_RESOURCE_NAME,
  adaptOrganizationNodeApi,
  isJavaOnlyOrganizationAction,
  isOrganizationsV2Enabled,
  remapOrganizationAction
} from './organizationServiceV2';

// Org Resources
export const genMock = genResource('mock', LOCAL_ROOT, {
  parentResources: [
    {
      resource: 'organizations',
      idCallback: () => "7b6ca193-5144-4835-a292-be30f744492b"
    }
  ]
});

// Resources
export const crops = genResource('crops', MASTERLIST_CROPS_URL, {
  cwfContextRegExpExclusions: [/\/crops/]
});
export const products = genResource('products', MASTERLIST_PROXY_URL);
export const product = genResource('product', `${CROPWISE_MASTERDATA}`);
export const productsMasterData = genResource(
  'products',
  `${CROPWISE_MASTERDATA}`
);
export const tags = genResource('flags', `${CROPWISE_MASTERDATA}`);
export const tag = genResource('flag', `${CROPWISE_MASTERDATA}`);
export const costPerAcres = genResource('cost-per-acres', MOCK_API_URL);
export const dashboards = genResource('dashboards', MOCK_API_URL);
export const imagery = genResource('imagery', MOCK_API_URL);
// `organization` / `organizations` route each request to Java or Node depending on
// `releaseBeOrganizationsV2` at the moment the request is made, so consumers import
// them exactly as before and never see the flag.
const organizationsJavaApi = genResource(
  'organizations',
  `${CROPWISE_PROXY_URL}/v1/base`,
  {
    cwfContextRegExpExclusions: [/\/organizations/]
  }
);
// Both list resources key their axios instance off the shared resource name, so this
// one replaces the instance registered just above. They are configured identically,
// which is what keeps that harmless.
const organizationsNodeApi = genResource(
  ORGANIZATION_V2_RESOURCE_NAME,
  CROPWISE_PROXY_V2_URL,
  {
    cwfContextRegExpExclusions: [/\/organizations/]
  }
);
const organizationJavaApi = genResource(
  'organization',
  `${CROPWISE_PROXY_URL}/v1/base`
);
const organizationNodeApi = adaptOrganizationNodeApi(
  genResource('organization', CROPWISE_PROXY_V2_URL)
);

const organizationRouting = {
  isV2: isOrganizationsV2Enabled,
  remapAction: remapOrganizationAction,
  isJavaOnlyAction: isJavaOnlyOrganizationAction
};

export const organizations = makeRoutedResource(
  organizationsJavaApi,
  organizationsNodeApi,
  organizationRouting
);
export const organization = makeRoutedResource(
  organizationJavaApi,
  organizationNodeApi,
  organizationRouting
);

export const LMOrganization = genResource(
  'organization/land-management-enabled',
  `${CROPWISE_PROXY_URL}/v1/base`
);
export const properties = genResource(
  'properties',
  `${CROPWISE_PROXY_URL}/v1/base`
);
export const propertiesV2 = genResource(
  'properties',
  `${CROPWISE_PROXY_URL}/v2/base`
);
export const propertiesV3 = genResource(
  'properties',
  `${CROPWISE_PROXY_URL}/v3/base`
);
export const propertiesV4 = genResource(
  'properties',
  `${CROPWISE_PROXY_URL}/v4/base`
);
export const dashboardWeather = genResource('dashboardWeather', MOCK_API_URL);
// `recommendation` / `recommendations` route each request to Java or Node depending
// on `releaseBeRecommendationsV2` at the moment the request is made, so consumers
// import them exactly as before and never see the flag.
const recommendationJavaApi = genResource(
  'recommendation',
  `${CROPWISE_PROXY_URL}/v1`
);
const recommendationsJavaApi = genResource(
  'recommendations',
  `${CROPWISE_PROXY_URL}/v1`
);
const recommendationNodeApi = genResource(
  RECOMMENDATION_V2_RESOURCE_NAME,
  CROPWISE_PROXY_V2_URL
);

const recommendationRouting = {
  isV2: isRecommendationsV2Enabled,
  remapAction: remapRecommendationAction,
  isJavaOnlyAction: isJavaOnlyRecommendationAction
};

export const recommendation = makeRoutedResource(
  recommendationJavaApi,
  recommendationNodeApi,
  recommendationRouting
);
export const recommendations = makeRoutedResource(
  recommendationsJavaApi,
  recommendationNodeApi,
  recommendationRouting
);
export const recommendationsSearch = genResource(
  'recommendations/search',
  `${CROPWISE_PROXY_URL}/v1`
);
export const documentDetails = genResource(
  'products',
  `${MASTERLIST_PROXY_URL}`
);
export const submission = genResource(
  'integrations/submission',
  `${CROPWISE_PROXY_V2_URL}`,
  {
    cwfContextRegExpExclusions: [/organizations/g],
    customCwfContext: true
  }
);
export const simplotMatch = genResource(
  'integrations/simplot/match',
  `${CROPWISE_PROXY_V2_URL}`,
  {
    cwfContextRegExpExclusions: [/organizations/g],
    customCwfContext: true
  }
);
export const simplotCropZones = genResource(
  'integrations/simplot/cropzones',
  `${CROPWISE_PROXY_V2_URL}`,
  {
    cwfContextRegExpExclusions: [/organizations/g],
    customCwfContext: true
  }
);

export const agrianFields = genResource(
  'integrations/agrian/fields',
  `${CROPWISE_PROXY_V2_URL}`,
  {
    cwfContextRegExpExclusions: [/organizations/g],
    customCwfContext: true
  }
);

export const agrianMatch = genResource(
  'integrations/agrian/match',
  `${CROPWISE_PROXY_V2_URL}`,
  {
    cwfContextRegExpExclusions: [/organizations/g],
    customCwfContext: true
  }
);

export const integrations = genResource(
  'integrations',
  `${CROPWISE_PROXY_V2_URL}`,
  {
    cwfContextRegExpExclusions: [/organizations/g],
    customCwfContext: true
  }
);

export const agrian = genResource(
  'integrations/agrian',
  `${CROPWISE_PROXY_V2_URL}`,
  {
    cwfContextRegExpExclusions: [/organizations/g],
    customCwfContext: true
  }
);

export const calAg = genResource(
  'integrations/calag',
  `${CROPWISE_PROXY_V2_URL}`,
  {
    cwfContextRegExpExclusions: [/organizations/g],
    customCwfContext: true
  }
);

export const incomingInvoice = genResource(
  'integrations/incoming-invoice',
  `${CROPWISE_PROXY_V2_URL}`,
  {
    cwfContextRegExpExclusions: [/organizations/g],
    customCwfContext: true
  }
);

export const documentData = genResource('documents', `${MASTERLIST_PROXY_URL}`);
export const farm = genResource('farm', `${CROPWISE_PROXY_URL}/v1/base`);
export const farms = genResource('farms', `${CROPWISE_PROXY_URL}/v1/base`);
export const field = genResource('field', `${CROPWISE_PROXY_URL}/v1/base`);
export const fieldV2 = genResource('field', `${CROPWISE_PROXY_URL}/v2/base`);
export const cropZone = genResource(
  'cropzone',
  `${CROPWISE_PROXY_URL}/v1/base`
);
export const cropZoneV2 = genResource(
  'cropzone',
  `${CROPWISE_PROXY_URL}/v2/base`
);
export const cropZoneV3 = genResource(
  'cropzone',
  `${CROPWISE_PROXY_URL}/v3/base`
);
export const tasks = genResource('tasks', `${CROPWISE_PROXY_URL}/v1/base`);
export const task = genResource('task', `${CROPWISE_PROXY_URL}/v1/base`);
export const taskProducts = genResource(
  'products-per-season',
  `${CROPWISE_PROXY_URL}/v1/base/tasks`
);

export const plans = genResource('plans', `${CROPWISE_PROXY_URL}/v1`);
export const plan = genResource('plan', `${CROPWISE_PROXY_URL}/v1`);

export const connectAPI = genResource(
  'johnDeere',
  `${CROPWISE_PROXY_URL}/v1/integration`
);

export const company = genResource('company', `${CROPWISE_PROXY_URL}/v1/base`);

// `invoice` / `invoices` route each request to Java or Node depending on
// `releaseBeInvoicesV2` at the moment the request is made, so consumers import them
// exactly as before and never see the flag.
const invoiceJavaApi = genResource('invoice', `${CROPWISE_PROXY_URL}/v1`);
const invoicesJavaApi = genResource('invoices', `${CROPWISE_PROXY_URL}/v1`);
const invoiceNodeApi = genResource(
  INVOICE_V2_RESOURCE_NAME,
  CROPWISE_PROXY_V2_URL
);

const invoiceRouting = {
  isV2: isInvoicesV2Enabled,
  remapAction: remapInvoiceAction
};

export const invoice = makeRoutedResource(
  invoiceJavaApi,
  invoiceNodeApi,
  invoiceRouting
);
export const invoices = makeRoutedResource(
  invoicesJavaApi,
  invoiceNodeApi,
  invoiceRouting
);
export const companies = genResource(
  'companies',
  `${CROPWISE_PROXY_URL}/v1/base`
);
export const equipment = genResource(
  'equipment',
  `${CROPWISE_PROXY_URL}/v1/base`
);

export const equipments = genResource(
  'equipments',
  `${CROPWISE_PROXY_URL}/v1/base`
);

export const weather = genResource(
  'weather',
  `${AG_CLEAR_APP_URL}/${"historical/hourly"}`
);
export const mcCain = genResource(
  'mcCain',
  `${CROPWISE_PROXY_URL}/v1/integration`
);

export const inventory = genResource(
  'inventory',
  `${CROPWISE_PROXY_URL}/v1/inventory`
);
export const inventoryProducts = genResource(
  'products',
  `${CROPWISE_PROXY_URL}/v1/inventory`
);
export const inventoryProduct = genResource(
  'product',
  `${CROPWISE_PROXY_URL}/v1/inventory`
);

export const inventoryProductPrices = genResource(
  'products-prices',
  `${CROPWISE_PROXY_URL}/v1/inventory`
);

export const reports = genResource('reports', `${CROPWISE_PROXY_URL}/v1/base`);

export const yields = genResource('yields', `${CROPWISE_PROXY_URL}/v2/base`);

export const embeddedReport = genResource(
  'reportInGroup',
  `${CROPWISE_PROXY_URL}/v1/integration/powerBi`
);

export const reiphisData = genResource('reiphis', `${MASTERLIST_PROXY_URL}`);
export const newProxyReiphisData = genResource(
  'reiphi',
  `${CROPWISE_PROXY_URL}/v1/base`
);

export const yieldPath = `${CROPWISE_PROXY_URL}/v2/yield`;

export const yieldList = genResource('list', yieldPath, {
  cwfContextRegExpExclusions: [/organizations/g, /yield/g],
  customCwfContext: true
});

export const yieldDashboard = genResource(
  'dashboard',
  `${CROPWISE_PROXY_URL}/v2/yield`,
  {
    cwfContextRegExpExclusions: [/organizations/g, /yield/g],
    customCwfContext: true
  }
);

export const yieldApi = genResource('yield', `${CROPWISE_PROXY_URL}/v2`, {
  cwfContextRegExpExclusions: [/organizations/g, /yield/g],
  customCwfContext: true
});

// genAxiosInstance
export const genProxyResource = (
  resourceName,
  { parentResources, action, urlVersion = '' } = {}
) =>
  genResource(resourceName, `${CROPWISE_PROXY_URL}${urlVersion}`, {
    parentResources,
    action
  });

export const genProxyV2Resource = (
  resourceName,
  { parentResources, action, urlVersion = '' } = {}
) =>
  genResource(resourceName, `${CROPWISE_PROXY_V2_URL}${urlVersion}`, {
    parentResources,
    action
  });

export { genResource };

export const hourlyHistoricalPath = `${AG_CLEAR_APP_URL}/${"historical/hourly"}`;
export const hourlyForecastPath = `${AG_CLEAR_APP_URL}/${"forecast/hourly"}`;

export const createChildApi = (api, endpoint) =>
  api.createChildApi({ action: endpoint });

export const getUnmappedCropZonesOperationsNumber = genResource(
  'unmappedCropZoneCount',
  `${CROPWISE_PROXY_URL}/v1/integration/johnDeere`,
  {}
);
export const getUnmappedFarms = genResource(
  'properties',
  `${CROPWISE_PROXY_URL}/v1/integration/johnDeere/`,
  {}
);

export const cropZoneMatcher = genResource(
  'matchCropzones/bulk',
  `${CROPWISE_PROXY_URL}/v1/integration/johnDeere`,
  {}
);

export const mappedOperations = genResource(
  'operations',
  `${CROPWISE_PROXY_URL}/v1/integration/johnDeere`,
  {}
);

export const johnDeereMissingPackageWeight = genResource(
  'missing-pw-summary',
  `${CROPWISE_PROXY_URL}/v1/integration/johnDeere/operations`,
  {}
);

export const getUnmappedCropZonesOperationsNumberV2 = genResource(
  'operations/count',
  `${CROPWISE_PROXY_V2_URL}/integrations/connect`
);
export const getUnmappedFarmsV2 = genResource(
  'operations/properties',
  `${CROPWISE_PROXY_V2_URL}/integrations/connect`
);

export const cropZoneMatcherV2 = genResource(
  'operations/applications',
  `${CROPWISE_PROXY_V2_URL}/integrations/connect`
);

export const mappedOperationsV2 = genResource(
  'operations',
  `${CROPWISE_PROXY_V2_URL}/integrations/connect`
);

export const johnDeereMissingPackageWeightV2 = genResource(
  'missing-package-weight-products-summary',
  `${CROPWISE_PROXY_V2_URL}/integrations/connect`
);

export const bulkCropZoneCreation = genResource(
  'cropzones',
  `${CROPWISE_PROXY_URL}/v1/base`,
  {}
);

export const bulkCropZoneUpdate = genResource(
  'bulk-update',
  `${CROPWISE_PROXY_V2_URL}/crop-zones`,
  {
    cwfContextRegExpExclusions: [/organizations/g],
    customCwfContext: true
  }
);

export const bulkCropZoneCreate = genResource(
  'bulk-create',
  `${CROPWISE_PROXY_V2_URL}/crop-zones`,
  {
    cwfContextRegExpExclusions: [/organizations/g],
    customCwfContext: true
  }
);

export const bulkCropZoneDelete = genResource(
  'bulk-delete',
  `${CROPWISE_PROXY_V2_URL}/crop-zones`,
  {
    cwfContextRegExpExclusions: [/organizations/g],
    customCwfContext: true
  }
);

// Preferences API resources
export const preferences = genResource('preferences', CROPWISE_PROXY_V2_URL, {
  cwfContextRegExpExclusions: [/organizations/g],
  customCwfContext: true
});

export const grisBoundaries = genResource(
  'gris/boundaries',
  CROPWISE_PROXY_V2_URL
);
